// The font control of a text tool (unify-ui ship 4a): the font button, the weight select and the "not yet print-tested"
// line (I4), where the tool's <select> was, and the font sheet behind the button (picker.js). One part, mounted by every
// text tool; it decides nothing about the document — it says what the visitor picked (onPick, onWeight) and shows the
// font the page hands it (setFont).
//
// Nothing of the core is imported (ruling R87, packages/ui/test/shell.test.js): the tool hands in `core`, the one object
// the core's `fonts/picker-core` subpath exports — MOODS, CJK_TAGS, classify, fontRank, fontKey, defaultWeight, curatedEntry,
// asListed.
//
//   const fonts = createFontControl({ root, S, core, getFont, getText, onPick, onWeight });
//     root      the element that holds #font (the button), #weight (the select) and #font-untested (the line)
//     S         the page's words (its composed table: the shared half holds the sheet's words)
//     getFont() the document's font record now;  getText() the text the sheet's bundled rows preview
//     onPick(f) a row was picked: the list's entry (bundled, or one of the 12), or a catalog font
//               { src: 'google', id, family, variant, bytes } — a row that IS an entry hands the entry (R95)
//     onWeight(record) the weight select changed: the record of that weight, as the list knows it (asListed — the
//               bundled weight of a bundled family is the bundled record again)
//   fonts.setList(curated)  the list has arrived: the button is enabled (until then it is disabled, so a tap can never
//                           open an empty sheet)
//   fonts.setFont()         paint the button, the weight select and the line from getFont()
//   fonts.markFailed(key)   a font whose last load failed: its row says so at the next open
import { createPicker, mountSheet } from './picker.js';
import { loadCatalog, peekCatalog } from './catalog.js';
import { untested, familyOf, weightChoice } from './picker-model.js';
import { esc } from './esc.js';

export function createFontControl({ root, S, core, getFont, getText, onPick, onWeight }) {
  const button = root.querySelector('#font'), weight = root.querySelector('#weight'), line = root.querySelector('#font-untested');
  let curated = null, picker = null, painted = null;
  weight.setAttribute('aria-label', S.weight);
  // the button's name is the row's label and the family it shows ("Font Noto Sans …"): inside its <label> the browser would
  // name it by the label's whole text — the row's word and the not-yet-print-tested line, never the family (R109)
  const rowLabel = root.closest && root.closest('label'), word = rowLabel && [...rowLabel.children].find((n) => n.tagName === 'SPAN' && n !== root);
  if (word) { word.id ||= 'font-label'; button.setAttribute('aria-labelledby', `${word.id} ${button.id}`); }
  line.textContent = S.untested;
  button.addEventListener('click', () => { if (picker) picker.open(getText(), getFont()); });
  weight.addEventListener('change', () => {
    const f = getFont(), family = familyOf(f, curated, core);
    onWeight(core.asListed({ src: 'google', id: family, variant: weight.value, name: '' }, curated));
  });

  // What the select holds now (the final review's M2): the family it was painted for and its weights. A render that
  // repaints the same family touches no option (an open dropdown stays open) and asks the catalog nothing again (offline
  // it would ask on every edit); only a new family, or new weights, rebuild the options.
  let held = null;
  async function syncWeight(f) {
    const key = `${f.src}|${familyOf(f, curated, core)}`;
    let fam;
    if (held && held.key === key && (held.fam || f.src === 'google' || !peekCatalog())) fam = held.fam;   // the same family: its weights are known (a bundled one whose catalog has come since is read again)
    else {
      let list = null;
      if (f.src === 'google') { try { list = await loadCatalog(); } catch { /* offline: no select */ } }
      else list = peekCatalog();                                       // a bundled font: never fetched for it
      if (painted !== f) return;                                       // another font landed while the catalog loaded
      // a Google record without a weight that IS a bundled family at the catalog's default weight (R95, M4): the list's own
      // record — handed to the page as a weight is, so the design holds the entry the worker builds in
      if (list && f.src === 'google' && !f.variant) { const listed = core.asListed(f, curated, list); if (listed !== f) { onWeight(listed); return; } }
      fam = (list && list.find((c) => c.family === familyOf(f, curated, core))) || null;
    }
    const { weights, value } = weightChoice(f, fam, curated, core);
    const opts = weights.join(',');
    if (!held || held.opts !== opts) weight.innerHTML = weights.map((w) => `<option value="${esc(w)}">${esc(S.weightName(w))}</option>`).join('');
    if (weight.value !== value) weight.value = value;
    weight.classList.toggle('hidden', weights.length < 2);
    held = { key, fam, opts };
  }
  function setFont() {
    const f = getFont();
    painted = f;
    button.textContent = curated ? familyOf(f, curated, core) : f.id;
    button.title = button.textContent;                                 // the family whole where the button's row cuts it (name-tag's one-row control: R109)
    line.classList.toggle('hidden', !curated || !untested(f, curated, core));
    if (curated) syncWeight(f); else weight.classList.add('hidden');
  }
  return {
    setList(list) {
      curated = list;
      picker = createPicker({ dialog: mountSheet(document), curated, S, core, onPick });
      button.disabled = false;
      setFont();
    },
    setFont,
    markFailed(key) { if (picker) picker.markFailed(key); },
  };
}
